import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plus, Edit, Trash2, Save, X, CheckCircle } from 'lucide-react';
import { colors } from '../theme';

const GerenciadorStatus = () => {
  const [statusList, setStatusList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cor: '#6B7280',
    ordem: 0,
    ativo: true,
    is_final: false
  });

  useEffect(() => {
    carregarStatus();
  }, []);

  const carregarStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('primaza_token');
      if (!token) {
        alert('Sessão expirada. Por favor, faça login novamente.');
        return;
      }
      const response = await axios.get(`${API}/status-pedido`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusList(response.data);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else {
        alert(`Erro ao carregar status: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('primaza_token');
      if (!token) {
        alert('Sessão expirada. Por favor, faça login novamente.');
        return;
      }
      
      if (editingId) {
        // Atualizar
        await axios.put(`${API}/status-pedido/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Criar
        await axios.post(`${API}/status-pedido`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      resetForm();
      carregarStatus();
    } catch (error) {
      console.error('Erro ao salvar status:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else {
        alert(`Erro ao carregar status: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (status) => {
    setFormData({
      nome: status.nome,
      cor: status.cor,
      ordem: status.ordem,
      ativo: status.ativo,
      is_final: status.is_final
    });
    setEditingId(status.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja deletar este status?')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.delete(`${API}/status-pedido/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      carregarStatus();
    } catch (error) {
      console.error('Erro ao deletar status:', error);
      alert('Erro ao deletar status');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cor: '#6B7280',
      ordem: 0,
      ativo: true,
      is_final: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle style={{ color: colors.primaza.secondary }}>
              Gerenciamento de Status
            </CardTitle>
            {!showForm && (
              <Button
                onClick={() => setShowForm(true)}
                style={{ backgroundColor: colors.primaza.primary, color: 'white' }}
              >
                <Plus size={16} className="mr-2" />
                Novo Status
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Formulário */}
          {showForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg bg-gray-50 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome">Nome do Status *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: EM ROTA DE ENTREGA"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="cor">Cor (Hex) *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="cor"
                      type="color"
                      value={formData.cor}
                      onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                      className="w-20"
                    />
                    <Input
                      type="text"
                      value={formData.cor}
                      onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                      placeholder="#6B7280"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="ordem">Ordem de Exibição *</Label>
                  <Input
                    id="ordem"
                    type="number"
                    value={formData.ordem}
                    onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) })}
                    min="0"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ativo"
                      checked={formData.ativo}
                      onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="ativo">Status Ativo</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_final"
                      checked={formData.is_final}
                      onChange={(e) => setFormData({ ...formData, is_final: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="is_final">Status Final (Ex: Entregue)</Label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X size={16} className="mr-2" />
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: colors.primaza.primary, color: 'white' }}
                >
                  <Save size={16} className="mr-2" />
                  {editingId ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          )}

          {/* Lista de Status */}
          <div className="space-y-2">
            {loading && !showForm ? (
              <p className="text-center py-4 text-gray-500">Carregando...</p>
            ) : statusList.length === 0 ? (
              <p className="text-center py-4 text-gray-500">Nenhum status cadastrado</p>
            ) : (
              statusList.map((status) => (
                <div
                  key={status.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: status.cor }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{status.nome}</span>
                        {status.is_final && (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle size={12} className="mr-1" />
                            Final
                          </Badge>
                        )}
                        {!status.ativo && (
                          <Badge variant="outline" className="text-xs text-gray-500">
                            Inativo
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">Ordem: {status.ordem}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(status)}
                      style={{ borderColor: colors.primaza.primary, color: colors.primaza.secondary }}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(status.id)}
                      className="text-red-600 hover:bg-red-50 border-red-300"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GerenciadorStatus;
